import type { MembershipAvailability } from "@/store/types";
import { isWorkspaceMembershipOffline } from "@/utils";

/**
 * JS ports of `sqlMembershipIsOffline` / `sqlMembershipIsOnline`, evaluated against a store row.
 * A missing availability row (the old LEFT JOIN null) counts as offline.
 */
export function membershipIsOffline(availability: MembershipAvailability | null | undefined): boolean {
  return isWorkspaceMembershipOffline({ handsFreeUpdatedAt: availability?.mostRecentHandsFreeStatusUpdatedAt });
}

export function membershipIsOnline(availability: MembershipAvailability | null | undefined): boolean {
  return availability != null && !membershipIsOffline(availability);
}

/**
 * The "not driving but reachable" bucket shared by the driver rosters: online, not present, and
 * not in the explicit hands-free-off or volume-muted buckets.
 */
export function membershipIsNotDriving(availability: MembershipAvailability | null | undefined): boolean {
  if (!availability || !membershipIsOnline(availability)) return false;
  if (availability.calculatedAvailability === "present") return false;
  if (availability.calculatedAvailability === "unknown") return true;
  if (availability.calculatedAvailability !== "notPresent") return false;
  const handsFree = availability.mostRecentHandsFreeStatusEnabled;
  if (handsFree === false) return false;
  return !(handsFree === true && availability.mostRecentVolumeAudible === false);
}

export function membershipIsHandsFreeOff(availability: MembershipAvailability | null | undefined): boolean {
  return (
    availability != null &&
    availability.calculatedAvailability === "notPresent" &&
    membershipIsOnline(availability) &&
    availability.mostRecentHandsFreeStatusEnabled === false
  );
}

export function membershipIsVolumeMuted(availability: MembershipAvailability | null | undefined): boolean {
  return (
    availability != null &&
    availability.calculatedAvailability === "notPresent" &&
    membershipIsOnline(availability) &&
    availability.mostRecentHandsFreeStatusEnabled === true &&
    availability.mostRecentVolumeAudible === false
  );
}

export function membershipIsDriving(availability: MembershipAvailability | null | undefined): boolean {
  return availability != null && availability.calculatedAvailability === "present" && membershipIsOnline(availability);
}
